import { createElement, useEffect, useState } from "react";
import { NodeProperties } from "./node/NodeProperties";
import { Alert, Box, Button, Snackbar, useTheme } from "@mui/material";
import useMapElementStyles from "./useMapElementStyles";
import { updateMapElementById } from "./helperFunctions/updateMapElementById";
import { EdgeProperties } from "./edge/EdgeProperties";
import { AreaProperties } from "./area/AreaProperties";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import { useTranslation } from "react-i18next";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { tokens } from "../../theme/theme";

export const MapElementPropertyManager = ({
  selectedElement,
  mapElements,
  setMapElements,
  setSelectedElement,
  disabled,
  show,
  elementSelection,
  setCurrentMode,
  setElementSelection,
  currentMode,
  showAlert,
}) => {
  const { t } = useTranslation();
  const styles = useMapElementStyles();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [element, setElement] = useState({});
  const [open, setOpen] = useState(false);
  const [outletError, setOutletError] = useState(false);

  useEffect(() => {
    if (selectedElement.type) {
      setElement(selectedElement);
      validate(selectedElement);
    }
  }, [selectedElement]);
  const elementTypes = {
    node: NodeProperties,
    edge: EdgeProperties,
    area: AreaProperties,
  };

  function handleClose() {
    setSelectedElement({});
  }

  // Second way to erase an element: directly from its inspection menu.
  // Mirrors the node-edge constraint check used by the Erase tool.
  function handleDeleteElement() {
    if (!element.type) return;
    if (element.type === "node") {
      const hasAttachedEdge = mapElements.some(
        (mapElement) =>
          mapElement.type === "edge" &&
          (mapElement.startNodeId === element.id ||
            mapElement.endNodeId === element.id)
      );
      if (hasAttachedEdge) {
        showAlert &&
          showAlert(
            t(
              "An edge has this knot attached. Disconnect the knot from the edge first."
            ),
            "warning"
          );
        return;
      }
    }
    setMapElements(mapElements.filter((el) => el.id !== element.id));
    setSelectedElement({});
  }

  function handleUpdate(value, propKey) {
    if (propKey === "props" && Array.isArray(value)) {
      const settingOutlet = value.find(
        (p) => p.key === "outlet_candidate" && p.value === true
      );
      if (settingOutlet) {
        const currentId = selectedElement.id || element.id;
        const outletExists = mapElements.some(
          (el) =>
            el.id !== currentId &&
            el.type === "node" &&
            el.props?.some((p) => p.key === "outlet_candidate" && p.value === true)
        );
        if (outletExists) {
          setOutletError(true);
          return;
        }
      }
    }

    if (validate({ ...element, [propKey]: value })) {
      setMapElements(
        updateMapElementById(mapElements, { ...element, [propKey]: value })
      );
    } else {
      setElement({ ...element, [propKey]: value });
    }
  }

  const [errors, setErrors] = useState(null);
  function validate(updatedElement) {
    var newErrors = {};
    // check name is unique
    mapElements.forEach((element) => {
      if (
        element.id !== updatedElement.id &&
        element.name === updatedElement.name
      ) {
        newErrors.name = t("Name has to be unique!");
      }
    });

    setErrors(newErrors);

    const numbOfErrors = Object.keys(newErrors);
    return numbOfErrors.length === 0;
  }

  if (!show) return;
  return (
    <>
    <Snackbar
      open={outletError}
      autoHideDuration={4000}
      onClose={() => setOutletError(false)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      <Alert severity="warning" onClose={() => setOutletError(false)}>
        {t("You can only select one outlet")}
      </Alert>
    </Snackbar>
    <Box
      sx={{ width: open ? "450px" : "0px", transition: "0.3s" }}
      className="relative h-full"
    >
      <Box
        className="border-r-2 h-full relative flex flex-col"
        sx={{
          borderColor: styles.selected,
        }}
      >
        {element.type && !disabled && (
          <Box
            className="flex justify-end p-2 border-b-2"
            sx={{ borderColor: styles.selected }}
          >
            <Button
              onClick={handleDeleteElement}
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlineIcon />}
            >
              {t("Delete")}
            </Button>
          </Box>
        )}
        <Box className="overflow-y-auto flex-1 min-h-0">
          {element.type ? (
            createElement(elementTypes[element.type], {
              element,
              onClose: () => setSelectedElement({}),
              onUpdate: handleUpdate,
              disabled,
              errors,
              setCurrentMode,
              setElementSelection,
              elementSelection,
              currentMode,
              mapElements,
            })
          ) : (
            <Empty />
          )}
        </Box>
      </Box>
      <Button
        sx={{
          position: "absolute",
          zIndex: 999,
          bgcolor: colors.contrast[100],
          padding: 0,
          minWidth: 0,
          borderRadius: "0 10px 10px 0",
        }}
        variant="contained"
        className=" bottom-24 right-0 translate-x-full h-10 w-10"
        onClick={() => setOpen(!open)}
      >
        <TroubleshootIcon sx={{ color: colors.primary[400] }} />
      </Button>
    </Box>
    </>
  );
};

const Empty = () => {
  const { t } = useTranslation();
  return (
    <Box className=" h-full flex flex-col justify-center items-center">
      <AdsClickIcon />
      {t("Select a Element first")}
    </Box>
  );
};
